import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { adminUpdateUser } from "@/integrations/supabase/edge";
import { Loader2 } from "lucide-react";

// All assignable organization roles
type AppMemberRole = "respondent" | "reviewer" | "portfolio_manager" | "client_admin";

// Role options for the dropdown
const ORG_ROLE_OPTIONS: { value: AppMemberRole; label: string; description: string }[] = [
  { value: "respondent", label: "Respondent", description: "Can fill out assessments" },
  { value: "reviewer", label: "Reviewer", description: "Can review assessments" },
  { value: "portfolio_manager", label: "Portfolio Manager", description: "Manages portfolios and their users" },
  { value: "client_admin", label: "Client Admin", description: "Administers sub-organizations and portfolios" },
];

interface Organization {
  id: string;
  name: string;
  sub_org_id?: string | null;
  client_id?: string | null;
}

interface SubOrg {
  id: string;
  name: string;
  client_id: string;
}

interface Client {
  id: string;
  name: string;
}

// Portfolio with sub_org info for cascading
interface Portfolio {
  id: string;
  name: string;
  sub_org_id: string | null;
}

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserCreated?: () => void;
  editUser?: {
    id: string;
    email: string;
    display_name?: string;
    org_id?: string;
    member_role?: string;
  } | null;
}

export const CreateUserModal = ({
  open,
  onOpenChange,
  onUserCreated,
  editUser
}: CreateUserModalProps) => {
  const [form, setForm] = useState({
    email: "",
    displayName: "",
    orgId: null as string | null,
    subOrgId: null as string | null,
    clientId: null as string | null,
    memberRole: "respondent" as AppMemberRole,
    tier: "tier_1" as string,
    tempPassword: "",
    forcePasswordReset: true,
  });
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [subOrgs, setSubOrgs] = useState<SubOrg[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Filtered options based on cascading selections
  const filteredSubOrgs = useMemo(() => {
    if (!form.clientId) return [];
    return subOrgs.filter(so => so.client_id === form.clientId);
  }, [subOrgs, form.clientId]);

  const filteredPortfolios = useMemo(() => {
    if (!form.subOrgId) return [];
    const filtered = portfolios.filter(p => p.sub_org_id === form.subOrgId);
    console.log("[CreateUserModal] Filtering portfolios for subOrgId:", form.subOrgId, "Result:", filtered, "All portfolios:", portfolios);
    return filtered;
  }, [portfolios, form.subOrgId]);

  // Initialize form when editUser changes or modal opens
  useEffect(() => {
    if (open && editUser) {
      setForm({
        email: editUser.email,
        displayName: editUser.display_name || "",
        orgId: editUser.org_id || null,
        subOrgId: null,
        clientId: null,
        memberRole: (editUser.member_role as AppMemberRole) || "respondent",
        tier: (editUser as any).tier || "tier_1",
        tempPassword: "",
        forcePasswordReset: true,
      });
    } else if (open) {
      setForm({
        email: "",
        displayName: "",
        orgId: null,
        subOrgId: null,
        clientId: null,
        memberRole: "respondent",
        tier: "tier_1",
        tempPassword: "",
        forcePasswordReset: true,
      });
    }
  }, [open, editUser]);

  // Load all data when modal opens
  useEffect(() => {
    if (open) {
      loadOrganizations();
      loadPortfolios();
      loadSubOrgs();
      loadClients();
    }
  }, [open]);

  const setField = <K extends keyof typeof form>(key: K, val: (typeof form)[K]) =>
    setForm(prev => ({ ...prev, [key]: val }));

  // For portfolio_manager we need a sub_org_id, for client_admin we need a client_id, for others we need an org_id
  const needsSubOrg = form.memberRole === "portfolio_manager";
  const needsClient = form.memberRole === "client_admin";
  const needsOrg = ["respondent", "reviewer"].includes(form.memberRole);

  const canSubmit = useMemo(() => {
    const hasEmail = !!form.email?.trim();
    const hasRequiredScope = needsSubOrg 
      ? !!form.subOrgId 
      : needsClient 
        ? !!form.clientId 
        : needsOrg 
          ? !!form.orgId 
          : true;
    return hasEmail && hasRequiredScope && !isSubmitting;
  }, [form.email, form.orgId, form.subOrgId, form.clientId, form.memberRole, isSubmitting, needsSubOrg, needsClient, needsOrg]);

  const loadOrganizations = async () => {
    setIsLoading(true);
    try {
      // Load portfolios via RPC (cast to any as RPC not in generated types yet)
      const { data, error } = await (supabase.rpc as any)("admin_list_portfolios");
      
      console.log("[CreateUserModal] admin_list_portfolios RPC result:", { data, error });
      
      if (error) throw error;
      // Map RPC result to expected shape
      const mapped = (data || []).map((p: { id: string; name: string; sub_org_id: string | null }) => ({
        id: p.id,
        name: p.name,
        sub_org_id: p.sub_org_id,
      }));
      console.log("[CreateUserModal] Mapped portfolios:", mapped);
      console.log("[CreateUserModal] Portfolios with sub_org_id:", mapped.filter((p: Portfolio) => p.sub_org_id));
      setOrganizations(mapped);
      setPortfolios(mapped);
    } catch (error) {
      console.error('Error loading organizations:', error);
      toast({
        title: "Error",
        description: "Failed to load organizations",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadPortfolios = async () => {
    // Already loaded in loadOrganizations
  };

  const loadSubOrgs = async () => {
    try {
      // Use RPC (cast to any as RPC not in generated types yet)
      const { data, error } = await (supabase.rpc as any)("admin_list_sub_orgs_all");
      
      if (error) throw error;
      // Map RPC result
      setSubOrgs((data || []).map((so: { id: string; name: string; client_id: string }) => ({
        id: so.id,
        name: so.name,
        client_id: so.client_id,
      })));
    } catch (error) {
      console.error('Error loading sub-organizations:', error);
    }
  };

  const loadClients = async () => {
    try {
      // Use RPC
      const { data, error } = await supabase.rpc("admin_list_clients");
      
      if (error) throw error;
      // Map RPC result
      setClients((data || []).map((c: { id: string; name: string }) => ({
        id: c.id,
        name: c.name,
      })));
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const handleSubmit = async () => {
    if (!form.email?.trim()) {
      toast({ title: "Missing email", description: "Email is required.", variant: "destructive" });
      return;
    }
    if (needsOrg && !form.orgId) {
      toast({ title: "Missing organization", description: "Organization is required for this role.", variant: "destructive" });
      return;
    }
    if (needsSubOrg && !form.subOrgId) {
      toast({ title: "Missing sub-organization", description: "Sub-organization is required for Portfolio Manager.", variant: "destructive" });
      return;
    }
    if (form.tempPassword && form.tempPassword.length < 6) {
      toast({ title: "Invalid password", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editUser) {
        // Update existing user
        const updatePayload: any = {
          user_id: editUser.id,
        };
        
        if (form.tempPassword) {
          updatePayload.password = form.tempPassword;
        }
        if (form.displayName?.trim()) {
          updatePayload.name = form.displayName.trim();
        }
        if (form.email.trim() !== editUser.email) {
          updatePayload.email = form.email.trim();
        }
        
        await adminUpdateUser(updatePayload);

        // Update role assignment based on role type
        await assignRoleToUser(editUser.id, form.memberRole);

        // If portfolio_manager, update tier
        if (form.memberRole === "portfolio_manager") {
          const { error: tierError } = await supabase
            .from("profiles")
            .update({ tier: form.tier })
            .eq("id", editUser.id);
          if (tierError) console.warn("Failed to update tier:", tierError);
        }

        toast({ title: "User updated", description: "User account has been updated." });
      } else {
        // Create new user via edge function
        // Determine org_id based on role type:
        // - respondent/reviewer: portfolio ID (form.orgId)
        // - portfolio_manager: sub-org ID (form.subOrgId)
        // - client_admin: client ID (form.clientId)
        let selectedOrgId: string | undefined;
        if (form.memberRole === 'client_admin') {
          selectedOrgId = form.clientId || undefined;
        } else if (form.memberRole === 'portfolio_manager') {
          selectedOrgId = form.subOrgId || undefined;
        } else {
          // respondent, reviewer, etc.
          selectedOrgId = form.orgId || undefined;
        }

        // Validate required org selection before submit
        if (!selectedOrgId) {
          const requiredField = form.memberRole === 'client_admin' 
            ? 'Parent Organization' 
            : form.memberRole === 'portfolio_manager' 
              ? 'Sub-Organization' 
              : 'Portfolio Company';
          toast({
            title: "Missing selection",
            description: `Please select a ${requiredField} for this role.`,
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }

        // Build request body per Edge Function contract
        const createPayload = {
          email: form.email.trim(),
          display_name: form.displayName?.trim() || undefined,
          org_id: selectedOrgId,                        // UUID string based on role
          roles: [form.memberRole],                     // must be array
          member_role: form.memberRole,                 // keep same value
          temp_password: form.tempPassword || undefined,
          force_password_reset: true,
        };

        // Determine whether to send portfolio_id or sub_org_id based on role
        const isPortfolioLevel = ['respondent', 'reviewer'].includes(form.memberRole);
        const assignmentKey = isPortfolioLevel ? 'portfolio_id' : 'sub_org_id';

        const edgePayload = {
          [assignmentKey]: selectedOrgId,
          email: createPayload.email.toLowerCase(),
          password: createPayload.temp_password || undefined,
          display_name: createPayload.display_name || null,
          member_role: form.memberRole || "respondent",
          // Tier is the source of truth — sent to edge function so the
          // profile is created with the correct tier atomically.
          tier: form.memberRole === "portfolio_manager" ? form.tier : undefined,
        };
        console.log("[create-user payload]", edgePayload);

        const { data: edgeData, error: edgeError } = await supabase.functions.invoke("create-user", {
          body: edgePayload,
        });

        if (edgeError) throw new Error(edgeError.message ?? "Edge function error");
        if (edgeData?.error) throw new Error(edgeData.error);

        toast({ title: "User created", description: "Account created and assigned to organization." });
      }

      onOpenChange(false);
      onUserCreated?.();
    } catch (e: any) {
      console.error('User operation error:', e);
      toast({
        title: "Error",
        description: e?.message || "Failed to process user",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Assign role to correct table based on role type
  const assignRoleToUser = async (userId: string, role: AppMemberRole) => {
    if (role === "portfolio_manager" && form.subOrgId) {
      // Portfolio manager goes in sub_org_memberships
      const { error } = await supabase
        .from('sub_org_memberships')
        .upsert({
          user_id: userId,
          sub_org_id: form.subOrgId,
          role: 'portfolio_manager'
        }, { onConflict: 'user_id,sub_org_id' });
      
      if (error) {
        console.error('Error assigning portfolio_manager role:', error);
        throw new Error(`Failed to assign portfolio manager role: ${error.message}`);
      }
    } else if (role === "client_admin" && form.clientId) {
      // Client admin goes in user_roles and client_memberships
      const { error: roleError } = await supabase
        .from('user_roles')
        .upsert({
          user_id: userId,
          role: 'client_admin'
        }, { onConflict: 'user_id,role' });
      
      if (roleError) {
        console.error('Error assigning client_admin role:', roleError);
        throw new Error(`Failed to assign client admin role: ${roleError.message}`);
      }

      // Add to client_memberships with the selected client
      const { error: membershipError } = await supabase
        .from('client_memberships')
        .upsert({
          user_id: userId,
          client_id: form.clientId,
          role: 'client_admin'
        }, { onConflict: 'user_id,client_id' });
      
      if (membershipError) {
        console.error('Error adding client_membership:', membershipError);
      }
    } else if (form.orgId && ["respondent", "reviewer"].includes(role)) {
      // Standard org roles go in org_memberships
      // Cast the role to the expected type (portfolio_manager is handled separately above)
      const memberRole = role as "respondent" | "reviewer";
      
      // First check if membership exists
      const { data: existing } = await supabase
        .from('org_memberships')
        .select('id')
        .eq('org_id', form.orgId)
        .eq('user_id', userId)
        .maybeSingle();
      
      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('org_memberships')
          .update({ member_role: memberRole })
          .eq('id', existing.id);
        
        if (error) {
          console.error('Error updating org role:', error);
          throw new Error(`Failed to update organization role: ${error.message}`);
        }
      } else {
        // Insert new - need to cast to any to bypass strict type checking
        const { error } = await (supabase as any)
          .from('org_memberships')
          .insert({
            user_id: userId,
            org_id: form.orgId,
            member_role: memberRole
          });
        
        if (error) {
          console.error('Error assigning org role:', error);
          throw new Error(`Failed to assign organization role: ${error.message}`);
        }
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editUser ? "Edit User" : "Create User"}</DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="user@example.com"
                disabled={!!editUser}
                className={editUser ? "bg-muted" : ""}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                value={form.displayName}
                onChange={(e) => setField("displayName", e.target.value)}
                placeholder="John Doe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="memberRole">Role *</Label>
              <Select 
                value={form.memberRole} 
                onValueChange={(value) => {
                  setField("memberRole", value as AppMemberRole);
                  // Clear scope selections when role changes
                  setField("orgId", null);
                  setField("subOrgId", null);
                  setField("clientId", null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ORG_ROLE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {ORG_ROLE_OPTIONS.find(o => o.value === form.memberRole)?.description}
              </p>
            </div>

            {/* Cascading dropdowns for respondent/reviewer: Client → Sub-Org → Portfolio */}
            {needsOrg && (
              <>
                {/* Step 1: Client Organization */}
                <div className="space-y-2">
                  <Label>Parent Organization *</Label>
                  <Select 
                    value={form.clientId ?? undefined} 
                    onValueChange={(value) => {
                      setField("clientId", value || null);
                      setField("subOrgId", null);
                      setField("orgId", null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 2: Sub-Organization (filtered by client) */}
                {form.clientId && (
                  <div className="space-y-2">
                    <Label>Sub-Organization *</Label>
                    <Select 
                      value={form.subOrgId ?? undefined} 
                      onValueChange={(value) => {
                        setField("subOrgId", value || null);
                        setField("orgId", null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sub-organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredSubOrgs.map(subOrg => (
                          <SelectItem key={subOrg.id} value={subOrg.id}>
                            {subOrg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {filteredSubOrgs.length === 0 && (
                      <p className="text-xs text-muted-foreground">No sub-organizations found for this client</p>
                    )}
                  </div>
                )}

                {/* Step 3: Portfolio Company (filtered by sub-org) */}
                {form.subOrgId && (
                  <div className="space-y-2">
                    <Label>Portfolio Company *</Label>
                    {filteredPortfolios.length === 0 ? (
                      <div className="rounded-md border border-dashed border-muted-foreground/25 p-3">
                        <p className="text-sm text-muted-foreground">
                          No portfolio companies are linked to this sub-organization yet.
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Create portfolios under this sub-organization first.
                        </p>
                      </div>
                    ) : (
                      <>
                        <Select 
                          value={form.orgId ?? undefined} 
                          onValueChange={(value) => setField("orgId", value || null)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select portfolio company" />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredPortfolios.map(portfolio => (
                              <SelectItem key={portfolio.id} value={portfolio.id}>
                                {portfolio.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!form.orgId && (
                          <p className="text-xs text-destructive">Please select a portfolio company</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Cascading dropdowns for portfolio_manager: Client → Sub-Org */}
            {needsSubOrg && (
              <>
                {/* Step 1: Client Organization */}
                <div className="space-y-2">
                  <Label>Parent Organization *</Label>
                  <Select 
                    value={form.clientId ?? undefined} 
                    onValueChange={(value) => {
                      setField("clientId", value || null);
                      setField("subOrgId", null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 2: Sub-Organization (filtered by client) */}
                {form.clientId && (
                  <div className="space-y-2">
                    <Label>Sub-Organization *</Label>
                    <Select 
                      value={form.subOrgId ?? undefined} 
                      onValueChange={(value) => setField("subOrgId", value || null)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sub-organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredSubOrgs.map(subOrg => (
                          <SelectItem key={subOrg.id} value={subOrg.id}>
                            {subOrg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {filteredSubOrgs.length === 0 && (
                      <p className="text-xs text-muted-foreground">No sub-organizations found for this client</p>
                    )}
                    {!form.subOrgId && filteredSubOrgs.length > 0 && (
                      <p className="text-xs text-destructive">Please select a sub-organization</p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Tier selector for portfolio_manager */}
            {needsSubOrg && (
              <div className="space-y-2">
                <Label>Tier</Label>
                <Select
                  value={form.tier}
                  onValueChange={(value) => setField("tier", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tier_1">Tier 1 — Starter (max 3 clients)</SelectItem>
                    <SelectItem value="tier_2">Tier 2 — Professional</SelectItem>
                    <SelectItem value="tier_3">Tier 3 — Enterprise</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Controls feature access and client limits
                </p>
              </div>
            )}

            {/* Show client dropdown for client_admin */}
            {needsClient && (
              <div className="space-y-2">
                <Label htmlFor="clientId">Client Organization *</Label>
                <Select 
                  value={form.clientId ?? undefined} 
                  onValueChange={(value) => setField("clientId", value || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!form.clientId && (
                  <p className="text-xs text-destructive">Please select a client organization</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="tempPassword">
                {editUser ? "Set New Password" : "Temporary Password"}
              </Label>
              <Input
                id="tempPassword"
                type="password"
                value={form.tempPassword}
                onChange={(e) => setField("tempPassword", e.target.value)}
                placeholder="Leave empty for auto-generated"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="forceReset"
                checked={form.forcePasswordReset}
                onCheckedChange={(checked) => setField("forcePasswordReset", checked === true)}
              />
              <Label htmlFor="forceReset" className="text-sm font-normal cursor-pointer">
                Require password reset on next login
              </Label>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {editUser ? "Updating..." : "Creating..."}
              </>
            ) : (
              editUser ? "Save Changes" : "Create User"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};